<style lang="scss">
  @use "../../../assets/css/design-system/colors";
  @use "../../../assets/css/design-system/mixins";
  @use "../../../assets/css/design-system/utils";
  @use "./StatusTag";

  div {
    width: fit-content;
    align-items: center;
    border-radius: 20px;
    display: inline-flex;
    justify-content: center;
    white-space: nowrap;

    @include colors.utility_classes();

    @mixin disabledInner() {
      background-color: colors.get(interfaceDisabledGrey_5);
      color: colors.get(textDisabledGrey);
    }

    @mixin disabled() {
      &.disabled {
        @include disabledInner();
        &.low-emphasis,
        &.high-emphasis {
          @include disabledInner();
        }
      }
    }

    &.truncate {
      max-width: 100%;
      justify-content: flex-start;
      overflow-x: hidden;
      white-space: nowrap;
    }

    .slot-wrapper {
      &.truncate {
        overflow-x: hidden;
        text-overflow: ellipsis;
      }
    }

    /// Kind and contrasts

    &.success {
      @include disabled();
      &.low-emphasis {
        @include colors.background-color(systemPositive_10);
        @include colors.color(systemPositive_100);
      }

      &.high-emphasis {
        @include colors.background-color(systemPositive_100);
        @include colors.color(textWhite);
      }
    }

    &.warning {
      @include disabled();
      &.low-emphasis {
        @include colors.background-color(systemWarning_10);
        @include colors.color(systemWarning_100);
      }

      &.high-emphasis {
        @include colors.background-color(systemWarning_100);
        @include colors.color(textWhite);
      }
    }

    &.error {
      @include disabled();
      &.low-emphasis {
        @include colors.background-color(systemError_10);
        @include colors.color(systemError_100);
      }

      &.high-emphasis {
        @include colors.background-color(systemError_100);
        @include colors.color(textWhite);
      }
    }

    &.information {
      @include disabled();
      &.low-emphasis {
        @include colors.background-color(systemInformation_10);
        @include colors.color(systemInformation_100);
      }

      &.high-emphasis {
        @include colors.background-color(systemInformation_100);
        @include colors.color(textWhite);
      }

      // TODO add separate interactive styles for low/high emphasis
      &.interactive {
        &:hover {
          @include colors.background-color(primary_10);
          @include colors.color(primary_70);
        }

        &.active {
          @include colors.background-color(primary_20);
          @include colors.color(primary_70);
        }
      }
    }

    &.meeting {
      @include colors.background-color(outcomeMeeting_5);
      @include colors.color(outcomeMeeting);
    }

    &.notInterested {
      @include colors.background-color(notInterested_5);
      @include colors.color(notInterested);
    }

    &.interested {
      @include colors.background-color(interested_5);
      @include colors.color(interested);
    }

    &.neutral {
      @include disabled();
      &.low-emphasis {
        @include colors.background-color(interfaceSwGrey_10);
        @include colors.color(textBlack);
      }

      &.high-emphasis {
        @include colors.background-color(interfaceSwShadowGrey);
        @include colors.color(textWhite);
      }

      // TODO add separate interactive styles for low/high emphasis
      &.interactive {
        &:hover {
          @include colors.background-color(interfaceSwGrey_20);
          @include colors.color(textBlack);
        }

        &.active {
          @include colors.background-color(interfaceSwGrey_20);
          @include colors.color(textBlack);
        }
      }
    }

    &.brand {
      @include disabled();
      &.low-emphasis {
        @include colors.background-color(primary_5);
        @include colors.color(primary_50);
      }

      &.high-emphasis {
        @include colors.background-color(primary_50);
        @include colors.color(textWhite);
      }
    }

    &.reply {
      background-color: colors.get(activityReply_5);
      color: colors.get(activityReply_50);
      @include disabled();
    }

    &.search {
      @include disabled();
      &.low-emphasis {
        @include colors.background-color(accentSearchPrimary);
        @include colors.color(textBlack);
      }
    }

    &.ai {
      @include disabled();
      &.low-emphasis {
        @include colors.background-color(aiSurfacePurple);
        @include colors.color(aiTextPurple);
      }
    }

    &.small {
      padding: 0 utils.in_rem(6px);
      height: utils.in_rem(16px);
      gap: utils.in_rem(4px);
      @include mixins.body3();
    }

    &.medium {
      padding: 0 utils.in_rem(8px);
      height: utils.in_rem(20px);
      gap: utils.in_rem(6px);
      @include mixins.body3();

      > img {
        margin-left: utils.in_rem(-5.5px);
        height: utils.in_rem(16px);
        width: utils.in_rem(16px);
        border-radius: 50%;
        object-fit: cover;
      }
    }

    &.large {
      padding: 0 utils.in_rem(12px);
      height: utils.in_rem(26px);
      gap: utils.in_rem(8px);
      @include mixins.body3();
    }
  }
</style>

<script>
  import Icon from "../Icon.svelte";

  let cls = "";
  export { cls as class };
  export let style = "";
  export let kind = "brand";
  export let size = "large";
  export let contrast = "low";
  export let leadingIcon = "";
  export let trailingIcon = "";
  export let leadingSrc = "";
  export let srcStyle = "";
  export let interactive = false;
  export let truncate = false; // to limit width of chip
  export let disabled = false;
  export let active = false;

  const iconSizeMap = {
    small: "ten-px",
    medium: "xs",
    large: "xs",
  };
</script>

<div
  class="{cls} {kind} {contrast}-emphasis {size}"
  class:interactive
  class:truncate
  class:disabled
  class:active
  {style}
>
  {#if leadingSrc}
    <img src={leadingSrc} style={srcStyle} alt="logo" />
  {/if}
  {#if leadingIcon}
    <Icon class={leadingIcon} size={iconSizeMap[size]} />
  {/if}
  <span class="slot-wrapper" class:truncate>
    <slot />
  </span>
  {#if trailingIcon}
    <Icon class={trailingIcon} size={iconSizeMap[size]} />
  {/if}
</div>
